import { CurrencyUtils } from '@Core/Currencies/CurrencyUtils';
import { gameInstance } from '@Game/GameComponent';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ISpecialGames, ISpinStep, IWinningLine, SPECIAL_GAME_MODE_FLAGS } from '@GFTypes/GameStoreTypes';
import { IActionResponseOutcome } from '@GFTypes/Responses/ActionResponse';
import { IPayTableSymbol } from '@GFTypes/Responses/AuthenticateResponse';
import { filterCoinPositionsForDoubleCoins, getNormalizedLockAndRollCoinPositions, normalizeScatterPositions } from '../Utils/SymbolIndexNormalizerUtils';
import { buildSelectionMaskFromPositions, getMinimumCountFromPaytable, getSpecialSymbolConfig, getSpecialSymbolId } from '../Utils/Utils';
import { getPositionsFromWinningLineByWinningReels } from './WinDataProcessor';
import { isDoubleLockAndRollCoinPresent } from './DoubleLockAndRollCoinDataProcessor';

export const getSpecialGames = (outcome: IActionResponseOutcome): ISpecialGames | undefined => {
    const clone = { ...outcome };
    const numberOfSymbolsPerReel = gameInstance.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);
    const gameConfig = gameInstance.gameStore.get(EGameStoreKeys.AuthenticateGameConfig);

    if (!clone.specialGames) return;

    if (!clone.specialGames.triggerPositions && !clone.specialGames.lockAndRollData) {
        const positions: [number, number][] = [];
        clone.steps?.at(0)?.scatters?.forEach((scatter) => positions.push(...normalizeScatterPositions(scatter.positions, numberOfSymbolsPerReel)));
        if (clone.specialGames.currentSpin === 0) clone.specialGames.triggerPositions = positions;
        else clone.specialGames.retriggerPositions = positions;
        const currentSpecialGamesType = gameInstance.gameStore.get(EGameStoreKeys.CurrentSpecialGamesType);
        const currentSpecialGamesFlag = currentSpecialGamesType && SPECIAL_GAME_MODE_FLAGS.get(currentSpecialGamesType);
        // this flag is not detected correctly on restore but it doesnt matter as it only says to reduce
        // spinsWon by previousSpinTotalSpins and previous spin on restore is unknown (so 0) anyway
        const isInSpecialGames = currentSpecialGamesFlag ? gameInstance.gameStore.get(currentSpecialGamesFlag) : false;
        const areSpinsWon = positions.length > 0;
        const isSpinsWonVariablePresentInResponse = !!clone.specialGames.spinsWon;
        const shouldAddSpinsWonVariableToResponse = areSpinsWon && !isSpinsWonVariablePresentInResponse;

        if (shouldAddSpinsWonVariableToResponse) {
            if (isInSpecialGames) {
                const previousSpinTotalSpins =
                    gameInstance.gameStore.get(EGameStoreKeys.SpinOutcome)?.specialGames?.totalSpins ||
                    gameInstance.gameStore.get(EGameStoreKeys.ResumedSpinOutcome)?.specialGames?.totalSpins ||
                    0;
                clone.specialGames.spinsWon = clone.specialGames.totalSpins - previousSpinTotalSpins;
            } else {
                clone.specialGames.spinsWon = clone.specialGames.totalSpins;
            }
        }
    }

    if (!clone.specialGames.triggerPositions && clone.specialGames.lockAndRollData) {
        const positions = getNormalizedLockAndRollCoinPositions(clone.specialGames.lockAndRollData.symbols, numberOfSymbolsPerReel) ?? [];
        const positionsForDoubleCoins = filterCoinPositionsForDoubleCoins(positions);
        clone.specialGames.triggerPositions = isDoubleLockAndRollCoinPresent(gameConfig) ? positionsForDoubleCoins : positions;
    }

    return clone.specialGames;
};

export const getStepsWithSpecialSymbolWinningLines = (steps: ISpinStep[], gameType: number): ISpinStep[] => {
    const lastStep = steps?.at(-1);

    if (!steps || !lastStep?.finalSymbols || !gameType) return steps;

    const specialSymbolId = getSpecialSymbolId(gameType);
    if (!specialSymbolId) return steps;

    const specialSymbolConfig = getSpecialSymbolConfig(specialSymbolId);
    if (!specialSymbolConfig) return steps;

    const minimumCount = getMinimumCountFromPaytable(specialSymbolConfig);

    const reelIndexes = getSpecialSymbolFilledReelIndexes(lastStep.finalVisibleSymbols, specialSymbolId, minimumCount);

    if (!reelIndexes) return steps;

    const winningLines = getSpecialSymbolWinningLines(reelIndexes, specialSymbolConfig, specialSymbolId, lastStep.finalVisibleSymbols);

    if (!winningLines.length) return steps;

    return steps.map((step, index) =>
        index === steps.length - 1
            ? {
                  ...step,
                  winningLines
              }
            : step
    );
};

export const getSpecialSymbolFilledReelIndexes = (
    finalVisibleSymbols: number[][] | undefined,
    specialSymbolId: number | undefined,
    minimumCount: number
): number[] | undefined => {
    if (!finalVisibleSymbols || !specialSymbolId || !minimumCount) return undefined;
    const matchedIndexes: number[] = [];
    for (let i = 0; i < finalVisibleSymbols.length; i++) {
        const visible = finalVisibleSymbols[i];
        if (visible.every((symbolId) => symbolId === specialSymbolId)) matchedIndexes.push(i);
    }
    return matchedIndexes.length >= minimumCount ? matchedIndexes : undefined;
};

function getSpecialSymbolWinningLines(
    reelIndexes: number[],
    symbolConfig: IPayTableSymbol,
    specialSymbolId: number,
    finalVisibleSymbols: number[][]
): IWinningLine[] {
    const { payLines } = gameInstance.gameStore.get(EGameStoreKeys.AuthenticateGameConfig);
    const numberOfReels = gameInstance.gameStore.get(EGameStoreKeys.NumberOfReels);

    const bet = gameInstance.gameStore.get(EGameStoreKeys.LastSpinBet);
    if (!bet) return [];

    const symbolsCount = reelIndexes.length;

    const countIndex = symbolConfig.paytable?.[0]?.count.indexOf(symbolsCount);

    if (countIndex == null || countIndex < 0) {
        return [];
    }

    const multiplier = symbolConfig.paytable?.[0]?.multiplier[countIndex];

    if (!multiplier) return [];

    const win = (multiplier * bet) / CurrencyUtils.getConfig().finalDivider;

    return payLines.map((_, id) => {
        const winningReels = getWinningReels(numberOfReels, symbolsCount, 1, reelIndexes);
        const positions = getPositionsFromWinningLineByWinningReels(_, winningReels);
        return {
            id,
            symbolsCount,
            win,
            direction: 1,
            reelIndexes,
            positions: positions,
            symbolId: specialSymbolId,
            winMessageMultiplier: 1,
            selectionMask: buildSelectionMaskFromPositions(positions, finalVisibleSymbols)
        };
    });
}

const getWinningReels = (numberOfReels: number, symbolsCount: number, direction: number, reelIndexes: number[]): number[] => {
    if (reelIndexes.length > 0) return reelIndexes;
    if (direction === 1) return Array.from({ length: symbolsCount }, (_, i) => i);
    return Array.from({ length: symbolsCount }, (_, i) => numberOfReels - symbolsCount + i);
};
